import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { formatPrice, formatDate } from "../utils/helpers";
import { uploadAPI, usersAPI, inviteCodesAPI, authAPI, salesAPI } from "../services/api";
import AdsAdmin from "./AdsAdmin.js";

export default function AdminPanel() {
  const { sales, products, getAdminStats, currentUser, approveUser, rejectUser, loadSales } = useApp();
  const [tab, setTab] = useState("usuarios");
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [newCodeDesc, setNewCodeDesc] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [sellerInfo, setSellerInfo] = useState({});

  useEffect(() => {
    if (currentUser?.role === "admin") {
      loadUsers();
      loadInviteCodes();
    }
  }, []);

  // El alias/CBU de cada vendedor (para saber donde liberarle el pago) ya viene en allUsers, no hace falta pedirlo aparte
  useEffect(() => {
    const map = {};
    allUsers.forEach(u => { map[u.id] = { mercadopago_alias: u.mercadopago_alias, mercadopago_cbu: u.mercadopago_cbu }; });
    setSellerInfo(map);
  }, [allUsers]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const { users } = await usersAPI.getAll();
      setPendingUsers(users.filter(u => u.status === "pending"));
      setAllUsers(users);
    } catch(e) {
      console.error(e);
    }
    setLoadingUsers(false);
  };

  const loadInviteCodes = async () => {
    try {
      const { codes } = await inviteCodesAPI.getAll();
      setInviteCodes(codes || []);
    } catch(e) {}
  };

  const handleApprove = async (userId) => {
    await approveUser(userId);
    await loadUsers();
  };

  const handleReject = async (userId) => {
    if (window.confirm("Rechazar este usuario?")) {
      await rejectUser(userId);
      await loadUsers();
    }
  };

  const handleAddCode = async () => {
    if (!newCode.trim()) return;
    await inviteCodesAPI.create(newCode, newCodeDesc);
    setNewCode(""); setNewCodeDesc("");
    loadInviteCodes();
  };

  const toggleCode = async (id) => {
    await inviteCodesAPI.toggle(id);
    loadInviteCodes();
  };

  const handleViewDni = async (userId) => {
    try {
      const { url } = await uploadAPI.getDniPhotoUrl(currentUser.id, userId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      alert("No se pudo abrir la foto: " + e.message);
    }
  };

  const handleRefund = async (sale) => {
    if (!window.confirm(`¿Reembolsar la venta de "${sale.productTitle}" a ${sale.buyerName} por ${formatPrice(sale.price)}? Se devuelve la plata por Mercado Pago y se le repone el stock al producto.`)) return;
    try {
      await salesAPI.refund(sale.id);
      alert("Reembolso realizado.");
      loadSales();
    } catch (e) {
      alert("No se pudo reembolsar: " + e.message);
    }
  };

  const handleGenerateResetLink = async (userId) => {
    try {
      const { link } = await authAPI.adminGenerateResetLink(userId);
      try {
        await navigator.clipboard.writeText(link);
        alert("Link copiado — pegalo y mandaselo por WhatsApp o donde prefieras. Vence en 1 hora.\n\n" + link);
      } catch {
        window.prompt("No se pudo copiar solo — copialo de aca (vence en 1 hora):", link);
      }
    } catch (e) {
      alert("No se pudo generar el link: " + e.message);
    }
  };

  if (currentUser?.role !== "admin") {
    return <div style={s.denied}><span style={{fontSize:48}}>🚫</span><p>Acceso restringido.</p></div>;
  }

  const { totalCommission, pendingCommission, totalSales, totalVolume, pendingRetained } = getAdminStats();
  // Pagadas por el comprador pero todavia sin entregar (plata retenida, hay que liberarla al confirmar)
  const pendingSales = sales.filter(s => s.paymentStatus === "paid" && s.status !== "received");
  // Ya confirmada la entrega, pero todavia no se le transfirio a mano su parte al vendedor
  const pendingPayouts = sales.filter(s => s.paymentStatus === "paid" && s.status === "received" && !s.sellerPaid);

  const handleMarkPaid = async (sale) => {
    try {
      await salesAPI.markPaid(sale.id);
      loadSales();
    } catch(e) {
      alert("No se pudo marcar como pagada: " + e.message);
    }
  };

  const renderPayoutCell = (sale) => {
    const seller = sellerInfo[sale.sellerId];
    if (!seller) return <span style={{color:"#aaa",fontSize:12}}>—</span>;
    if (seller.mercadopago_alias) return <span style={{color:"#1D9E75",fontWeight:500}}>💳 {seller.mercadopago_alias}</span>;
    if (seller.mercadopago_cbu)   return <span style={{color:"#888",fontSize:12}}>{seller.mercadopago_cbu}</span>;
    return <span style={{color:"#E24B4A",fontSize:12}}>⚠️ Sin alias cargado</span>;
  };

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Panel de administracion</h1>
      <p style={s.sub}>Shalom 👋</p>

      {pendingUsers.length > 0 && (
        <div style={s.alertBox}>
          🔔 Tenes <strong>{pendingUsers.length}</strong> usuario{pendingUsers.length!==1?"s":""} esperando aprobacion
        </div>
      )}

      <div style={s.kpiRow}>
        {[
          { label:"Comision cobrada", value:formatPrice(totalCommission), icon:"💰", color:"#1D9E75" },
          { label:"Comision pendiente", value:formatPrice(pendingCommission), icon:"⏳", color:"#f59e0b" },
          { label:"Dinero retenido", value:formatPrice(pendingRetained), icon:"🔒", color:"#8b5cf6" },
          { label:"Ventas totales", value:totalSales, icon:"🛒", color:"#3b82f6" },
        ].map(k => (
          <div key={k.label} style={s.kpiCard}>
            <div style={s.kpiIcon}>{k.icon}</div>
            <div>
              <p style={{...s.kpiValue,color:k.color}}>{k.value}</p>
              <p style={s.kpiLabel}>{k.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div style={s.tabs}>
        {[
          ["usuarios", `Usuarios${pendingUsers.length>0?` (${pendingUsers.length})`:""}`],
          ["ventas","Ventas pendientes"],
          ["pagos", `Pagos pendientes${pendingPayouts.length>0?` (${pendingPayouts.length})`:""}`],
          ["historial","Historial"],
          ["codigos","Codigos"],
          ["productos","Productos"],
          ["publicidades","Publicidades"],
        ].map(([id,label]) => (
          <button key={id} style={{...s.tab,...(tab===id?s.tabActive:{})}} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab === "usuarios" && (
        <div>
          {loadingUsers ? <p style={s.empty}>Cargando...</p> : (
            <>
              {pendingUsers.length > 0 && (
                <>
                  <p style={s.sectionTitle}>⏳ Pendientes de aprobacion ({pendingUsers.length})</p>
                  <div style={s.userList}>
                    {pendingUsers.map(u => (
                      <div key={u.id} style={s.userCardPending}>
                        <div style={s.userTop}>
                          <div style={s.userAvatar}>{u.name.charAt(0).toUpperCase()}</div>
                          <div style={s.userInfo}>
                            <p style={s.userName}>{u.name}</p>
                            <p style={s.userEmail}>{u.email}</p>
                            <p style={s.userDate}>Registrado el {formatDate(u.created_at)}</p>
                          </div>
                          <div style={s.userActions}>
                            <button style={s.btnApprove} onClick={() => handleApprove(u.id)}>✅ Aprobar</button>
                            <button style={s.btnReject} onClick={() => handleReject(u.id)}>❌ Rechazar</button>
                          </div>
                        </div>
                        <div style={s.communityInfo}>
                          <div style={s.communityItem}>
                            <span style={s.communityIcon}>🕍</span>
                            <div>
                              <p style={s.communityLabel}>Comunidad</p>
                              <p style={s.communityValue}>{u.community || "No indicada"}</p>
                            </div>
                          </div>
                          <div style={s.communityItem}>
                            <span style={s.communityIcon}>✡️</span>
                            <div>
                              <p style={s.communityLabel}>Rabino</p>
                              <p style={s.communityValue}>{u.rabbi || "No indicado"}</p>
                            </div>
                          </div>
                          <div style={s.communityItem}>
                            <span style={s.communityIcon}>🪪</span>
                            <div>
                              <p style={s.communityLabel}>DNI</p>
                              <p style={s.communityValue}>{u.dni || "No indicado"}</p>
                              {u.dni_photo_id && (
                                <button style={s.btnViewDni} onClick={() => handleViewDni(u.id)}>🪪 Ver foto</button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <p style={{...s.sectionTitle,marginTop:24}}>👥 Todos los usuarios ({allUsers.length})</p>
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead>
                    <tr>{["Nombre","Email","Comunidad","Rabino","Estado","Registro","DNI","Contraseña"].map(h=><th key={h} style={s.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {allUsers.map(u => (
                      <tr key={u.id} style={s.tr}>
                        <td style={s.td}>{u.name}</td>
                        <td style={s.td}>{u.email}</td>
                        <td style={s.td}>{u.community || "—"}</td>
                        <td style={s.td}>{u.rabbi || "—"}</td>
                        <td style={s.td}>
                          <span style={{...s.statusBadge,
                            background: u.status==="approved"?"#E1F5EE":u.status==="rejected"?"#FEE":"#fff8e1",
                            color: u.status==="approved"?"#0F6E56":u.status==="rejected"?"#c0392b":"#b7830a"
                          }}>
                            {u.status==="approved"?"✅ Aprobado":u.status==="rejected"?"❌ Rechazado":"⏳ Pendiente"}
                          </span>
                        </td>
                        <td style={s.td}>{formatDate(u.created_at)}</td>
                        <td style={s.td}>
                          {u.dni_photo_id
                            ? <button style={s.btnViewDni} onClick={() => handleViewDni(u.id)}>🪪 Ver DNI</button>
                            : <span style={{color:"#aaa",fontSize:12}}>—</span>}
                        </td>
                        <td style={s.td}>
                          <button style={s.btnViewDni} onClick={() => handleGenerateResetLink(u.id)}>🔑 Restablecer</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "codigos" && (
        <div>
          <p style={s.sectionTitle}>Codigos de invitacion</p>
          <div style={s.codesGrid}>
            {inviteCodes.map(code => (
              <div key={code.id} style={{...s.codeCard,...(!code.active?s.codeInactive:{})}}>
                <p style={s.codeText}>{code.code}</p>
                <p style={s.codeDesc}>{code.description || "Sin descripcion"}</p>
                <p style={s.codeUses}>Usado {code.uses || 0} veces</p>
                <button
                  style={{...s.btnToggle, background:code.active?"#FEE":"#E1F5EE", color:code.active?"#c0392b":"#0F6E56"}}
                  onClick={() => toggleCode(code.id, code.active)}
                >
                  {code.active ? "Desactivar" : "Activar"}
                </button>
              </div>
            ))}
          </div>
          <p style={{...s.sectionTitle,marginTop:24}}>Agregar nuevo codigo</p>
          <div style={s.newCodeBox}>
            <input style={{...s.inputSmall,textTransform:"uppercase",letterSpacing:2,fontWeight:600}} value={newCode} onChange={e=>setNewCode(e.target.value.toUpperCase())} placeholder="Ej: HANUKA2024" />
            <input style={s.inputSmall} value={newCodeDesc} onChange={e=>setNewCodeDesc(e.target.value)} placeholder="Descripcion (opcional)" />
            <button style={s.btnAdd} onClick={handleAddCode}>+ Agregar</button>
          </div>
        </div>
      )}

      {tab === "ventas" && (
        pendingSales.length === 0 ? <p style={s.empty}>No hay pagos retenidos esperando entrega.</p> : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>{["Fecha","Producto","Vendedor/a","Alias/CBU para liberar","Comprador/a","Cantidad","Total","Comision",""].map(h=><th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {pendingSales.map(sale => (
                  <tr key={sale.id} style={s.tr}>
                    <td style={s.td}>{formatDate(sale.date)}</td>
                    <td style={s.td}>{sale.productTitle}</td>
                    <td style={s.td}>{sale.sellerName}</td>
                    <td style={s.td}>{renderPayoutCell(sale)}</td>
                    <td style={s.td}>{sale.buyerName}</td>
                    <td style={s.td}>{sale.cantidad}</td>
                    <td style={s.td}>{formatPrice(sale.price)}</td>
                    <td style={{...s.td,color:"#1D9E75",fontWeight:600}}>{formatPrice(sale.commission)}</td>
                    <td style={s.td}>
                      <button style={s.btnReject} onClick={() => handleRefund(sale)}>↩️ Reembolsar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "pagos" && (
        pendingPayouts.length === 0 ? <p style={s.empty}>No hay pagos pendientes de transferir.</p> : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>{["Entrega confirmada","Producto","Vendedor/a","Alias/CBU para pagar","Monto a transferir",""].map(h=><th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {pendingPayouts.map(sale => (
                  <tr key={sale.id} style={s.tr}>
                    <td style={s.td}>{formatDate(sale.receivedDate)}</td>
                    <td style={s.td}>{sale.productTitle}</td>
                    <td style={s.td}>{sale.sellerName}</td>
                    <td style={s.td}>{renderPayoutCell(sale)}</td>
                    <td style={{...s.td,color:"#1D9E75",fontWeight:600}}>{formatPrice(sale.sellerReceives)}</td>
                    <td style={s.td}>
                      <button style={s.btnApprove} onClick={() => handleMarkPaid(sale)}>✅ Ya le pague</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "historial" && (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
            <tr>{["Fecha","Producto","Vendedor/a","Alias/CBU","Comprador/a","Total","Comision","Pago","Entrega"].map(h=><th key={h} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {sales.map(sale => (
                <tr key={sale.id} style={s.tr}>
                  <td style={s.td}>{formatDate(sale.date)}</td>
                  <td style={s.td}>{sale.productTitle}</td>
                  <td style={s.td}>{sale.sellerName}</td>
                  <td style={s.td}>{renderPayoutCell(sale)}</td>
                  <td style={s.td}>{sale.buyerName}</td>
                  <td style={s.td}>{formatPrice(sale.price)}</td>
                  <td style={{...s.td,color:"#1D9E75",fontWeight:600}}>{formatPrice(sale.commission)}</td>
                  <td style={s.td}>
                    <span style={{...s.statusBadge,background:sale.paymentStatus==="paid"?"#E1F5EE":"#fff8e1",color:sale.paymentStatus==="paid"?"#0F6E56":"#b7830a"}}>
                      {sale.paymentStatus==="paid"?"✅ Pagado":"⏳ Sin pagar"}
                    </span>
                  </td>
                  <td style={s.td}>
                    <span style={{...s.statusBadge,background:sale.status==="received"?"#E1F5EE":"#fff8e1",color:sale.status==="received"?"#0F6E56":"#b7830a"}}>
                      {sale.status==="received"?"✅ Confirmada":"⏳ Pendiente"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "productos" && (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>{["Producto","Vendedor/a","Categoria","Precio","Stock","Estado"].map(h=><th key={h} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} style={s.tr}>
                  <td style={s.td}>{p.title}</td>
                  <td style={s.td}>{p.sellerName}</td>
                  <td style={s.td}>{p.category}</td>
                  <td style={s.td}>{formatPrice(p.price)}</td>
                  <td style={s.td}>{p.quantity}</td>
                  <td style={s.td}>
                    <span style={{...s.statusBadge,background:p.sold?"#FEE":"#E1F5EE",color:p.sold?"#c0392b":"#0F6E56"}}>
                      {p.sold?"Agotado":"Disponible"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "publicidades" && <AdsAdmin />}

    </div>
  );
}

const s = {
  page:{maxWidth:1100,margin:"0 auto",padding:"32px 20px"},
  heading:{fontSize:24,fontWeight:600,marginBottom:4},
  sub:{fontSize:14,color:"#888",marginBottom:16},
  alertBox:{background:"#fff8e1",border:"0.5px solid #f0c040",borderRadius:10,padding:"12px 16px",marginBottom:20,fontSize:14,color:"#b7830a"},
  kpiRow:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:16,marginBottom:28},
  kpiCard:{background:"#fff",border:"0.5px solid #e0e0e0",borderRadius:12,padding:"18px 20px",display:"flex",alignItems:"center",gap:14},
  kpiIcon:{fontSize:32},
  kpiValue:{fontSize:22,fontWeight:700},
  kpiLabel:{fontSize:12,color:"#888",marginTop:2},
  tabs:{display:"flex",gap:8,marginBottom:24,flexWrap:"wrap"},
  tab:{padding:"8px 14px",borderRadius:8,border:"0.5px solid #ddd",background:"#fff",cursor:"pointer",fontSize:13,color:"#555"},
  tabActive:{background:"#1D9E75",color:"#fff",borderColor:"#1D9E75",fontWeight:500},
  sectionTitle:{fontSize:15,fontWeight:600,color:"#333",marginBottom:12},
  userList:{display:"flex",flexDirection:"column",gap:12,marginBottom:16},
  userCardPending:{background:"#fff",border:"1.5px solid #1D9E75",borderRadius:12,padding:"16px 20px"},
  userTop:{display:"flex",alignItems:"center",gap:14,marginBottom:14},
  userAvatar:{width:44,height:44,borderRadius:"50%",background:"#E1F5EE",color:"#0F6E56",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:600,flexShrink:0},
  userInfo:{flex:1},
  userName:{fontSize:15,fontWeight:600,color:"#111"},
  userEmail:{fontSize:13,color:"#888"},
  userDate:{fontSize:12,color:"#aaa",marginTop:2},
  userActions:{display:"flex",gap:8,flexShrink:0},
  btnApprove:{background:"#1D9E75",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:600,cursor:"pointer"},
  btnReject:{background:"#FEE",color:"#c0392b",border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,cursor:"pointer"},
  communityInfo:{display:"flex",gap:16,background:"#f9f9f9",borderRadius:8,padding:"12px 14px",flexWrap:"wrap"},
  communityItem:{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:200},
  communityIcon:{fontSize:20},
  communityLabel:{fontSize:11,color:"#aaa",marginBottom:2},
  communityValue:{fontSize:14,fontWeight:500,color:"#333"},
  codesGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12},
  codeCard:{background:"#f0faf6",border:"1px solid #b0dcc9",borderRadius:12,padding:"16px",textAlign:"center"},
  codeInactive:{opacity:0.5,background:"#f5f5f5",borderColor:"#ddd"},
  codeText:{fontSize:22,fontWeight:700,color:"#0F6E56",letterSpacing:2,marginBottom:6},
  codeDesc:{fontSize:12,color:"#888",marginBottom:4},
  codeUses:{fontSize:12,color:"#aaa",marginBottom:10},
  btnToggle:{border:"none",borderRadius:8,padding:"6px 14px",fontSize:12,cursor:"pointer",fontWeight:500},
  newCodeBox:{display:"flex",flexDirection:"column",gap:10,maxWidth:400},
  inputSmall:{padding:"10px 12px",borderRadius:8,border:"0.5px solid #ddd",fontSize:14,width:"100%"},
  btnAdd:{background:"#1D9E75",color:"#fff",border:"none",borderRadius:8,padding:"10px 20px",fontSize:14,fontWeight:500,cursor:"pointer",alignSelf:"flex-start"},
  btnViewDni:{background:"#f0faf6",color:"#1D9E75",border:"0.5px solid #b0dcc9",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:500,cursor:"pointer",whiteSpace:"nowrap"},
  tableWrap:{overflowX:"auto",borderRadius:10,border:"0.5px solid #e0e0e0"},
  table:{width:"100%",borderCollapse:"collapse",background:"#fff",fontSize:13},
  th:{textAlign:"left",padding:"12px 14px",borderBottom:"0.5px solid #e0e0e0",fontWeight:500,color:"#555",background:"#fafafa"},
  tr:{borderBottom:"0.5px solid #f0f0f0"},
  td:{padding:"12px 14px",color:"#333"},
  statusBadge:{padding:"3px 10px",borderRadius:20,fontSize:12,fontWeight:500},
  empty:{padding:"40px",textAlign:"center",color:"#888",fontSize:14},
  denied:{textAlign:"center",padding:"80px 0",display:"flex",flexDirection:"column",alignItems:"center",gap:12,color:"#888"},
};